import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@src/components/Card';
import apis from '@src/apis';
import 'moment/locale/vi';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import NotificationDialog from '@src/components/NewNotificationDialog';
import { LANDING_PAGE_URL, NOTIFICATION_URL } from '@src/configs';
import actions from '@src/redux/actions';
import OTPConfirm from '@src/components/OTPConfirm';
import { sortBy } from 'lodash';
import sanitize from 'sanitize-html';
import { DEVICE_STATUS, TYPE_LOGOUT_SESSION } from '@src/constants/device';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import TypographyText from '@src/new-components/Typography';

import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';

import {
  StyledManagerDevice,
  StyledWapper,
  StyledActionWarning,
} from './index.styled';
import DeviceItem from './DeviceItem';
import DeviceSelect from './DeviceSelect';

const NewManageDevice = ({
  dialogDevice,
  isPopup,
  isExceedDevices,
  checkUserDevice,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const userInfo = useSelector((state) => state.auth.user || {});
  const { fingerprint, status = '' } = useSelector(
    (state) => state.deviceV2 || {},
  );
  const [devices, setDevices] = useState([]);
  const [totalFetchDevices, setTotalFetchDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [loginDevices, setLoginDevices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showConfirmLogoutDialog, setShowConfirmLogoutDialog] = useState(false);
  const [showRequestLogutDevice, setShowRequestLogutDevice] = useState(false);
  const { keycloak } = useCustomKeycloak();

  const responseDevice = useSelector((state) => state.device);
  const dispatch = useDispatch();
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [hasSelectDevice, setHasSelectDevice] = useState(false);
  const [openLogoutAllDevicesDialog, setOpenLogoutAllDevicesDialog] =
    useState(false);
  const [typeLogout, setTypeLogout] = useState(undefined);

  const ws = useRef(null);

  const fetchDevices = async () => {
    setLoading(true);
    const data = await apis.devices.getDevices({
      query: {
        'status[$in]': [DEVICE_STATUS.ONLINE, DEVICE_STATUS.RESTRICTED],
      },
    });
    if (data?.status) {
      // List all devices that are online or restricted with the current device
      const listDevice = data?.result?.devices?.filter(
        (device) =>
          device?.status === DEVICE_STATUS.ONLINE ||
          device?.fingerprint === fingerprint,
      );

      setDevices(listDevice);
      setTotalFetchDevices([...new Set([...devices, ...listDevice])]);
      setLoginDevices(listDevice.length);
    }
    setLoading(false);
  };

  const { getFeatureValue } = useFeatureFlags();
  const useUsageOptions = getFeatureValue(
    FEATURE_KEYS.USE_USAGE_OPTIONS_IN_PACKAGE,
    { userId: userInfo.id, packageCode: userInfo.packageCode },
  );

  const showLogoutAllDevices = !isPopup;
  const showLogoutAllDevicesMobile = showLogoutAllDevices && isMobile;
  const showLogoutAllDevicesDesktop = showLogoutAllDevices && !isMobile;

  const currentDevice = devices.find(
    (device) => device.fingerprint === fingerprint,
  );
  const otherDevices = devices.filter(
    (device) => device.fingerprint !== fingerprint,
  );
  const allOtherDeviceSelected = selectedDevices.length === otherDevices.length;

  const maxDevices = useUsageOptions
    ? userInfo?.studioUsageOptions?.device
    : userInfo?.maxDevices;

  const logoutDeviceWithOTP = async (otp) => {
    const result = await apis.devices.logoutWithOTP(otp);
    setShowRequestLogutDevice(!result);
    if (result) {
      if (typeLogout === TYPE_LOGOUT_SESSION.ALL) {
        await apis.session.logoutAllSessions();
        keycloak.logout({ redirectUri: LANDING_PAGE_URL, fingerprint });
      } else {
        await fetchDevices();
      }
      if (isPopup) {
        checkUserDevice();
      }
    }
    return result;
  };

  const callApiLogoutDevices = async (deviceIds, type) => {
    try {
      const typeLogoutValue =
        type === TYPE_LOGOUT_SESSION.ALL ? TYPE_LOGOUT_SESSION.ALL : undefined;
      const response = await apis.devices.sendEmailConfirmLogoutDevices(
        userInfo.email,
        deviceIds,
        typeLogoutValue,
      );
      if (response?.status) {
        setOpenLogoutAllDevicesDialog(false);
        setShowRequestLogutDevice(true);
      } else {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'errorOccurred',
          }),
        );
      }
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    }
  };

  const handleShowConfirmLogoutDialog = (deviceInfo) => {
    setSelectedDevice(deviceInfo);
    setShowConfirmLogoutDialog(true);
  };

  const handleCloseConfirmLogoutDialog = () => {
    setSelectedDevice(null);
    setShowConfirmLogoutDialog(false);
  };

  const handleLogout = async (currentFingerprint) => {
    await apis.devices.deactivateDevice(currentFingerprint);
    keycloak.logout({
      redirectUri: LANDING_PAGE_URL,
      fingerprint: currentFingerprint,
    });
  };

  const handleConfirmLogoutDevices = async (type) => {
    setShowConfirmLogoutDialog(false);

    const isCurrentDevice = selectedDevice?.fingerprint === fingerprint;

    if (isCurrentDevice) {
      await handleLogout(selectedDevice?.fingerprint);
      return;
    }

    const deviceIds = hasSelectDevice ? selectedDevices : [selectedDevice?.id];
    await callApiLogoutDevices(deviceIds, type);
    setTypeLogout(type);
  };

  const handleSelectDevice = (id) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (allOtherDeviceSelected) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(otherDevices.map((device) => device.id));
    }
  };

  const handleSelectOtherDevice = () => {
    setHasSelectDevice(true);
  };

  const handleCancelSelectOtherDevice = () => {
    setHasSelectDevice(false);
    setSelectedDevices([]);
  };

  const handleShowConfirmLogoutMulteDialog = () => {
    setShowConfirmLogoutDialog(true);
  };

  const renderConfirmLogoutDevicesAction = (handleClose, handleConfirm) => (
    <StyledActionWarning>
      <Button className="cancel-btn" variant="outlined" onClick={handleClose}>
        {t('cancel')}
      </Button>
      <Button className="agree-btn" variant="contained" onClick={handleConfirm}>
        {t('logout')}
      </Button>
    </StyledActionWarning>
  );

  const renderContentLogoutDevice = () => {
    const showMultiDeviceWarning =
      selectedDevices.length > 1 && hasSelectDevice;
    const translationKey = showMultiDeviceWarning
      ? 'logoutMultiDeviceWarning'
      : 'logoutWarning';

    return <Typography>{t(translationKey)}</Typography>;
  };

  useEffect(() => {
    if (fingerprint) fetchDevices();
  }, [fingerprint]);

  useEffect(() => {
    ws.current = new WebSocket(NOTIFICATION_URL);
  }, []);

  useEffect(() => {
    const newDevices = devices.map((device) => {
      if (
        responseDevice[device.fingerprint]?.status &&
        device.status !== responseDevice[device.fingerprint]?.status
      ) {
        return {
          ...device,
          status: responseDevice[device.fingerprint]?.status,
        };
      }
      return device;
    });

    const newTotalFetchDevices = totalFetchDevices.map((device) => {
      if (
        responseDevice[device.fingerprint]?.status &&
        device.status !== responseDevice[device.fingerprint]?.status
      ) {
        return {
          ...device,
          status: responseDevice[device.fingerprint]?.status,
        };
      }
      return device;
    });

    setDevices(newDevices);
    setTotalFetchDevices(newTotalFetchDevices);
    dispatch(actions.device.removeDevice(Object.keys(responseDevice)));
  }, [responseDevice]);

  const renderContent = () => (
    <TypographyText variant="body-16-regular">
      {t('confirmLogoutAllDevices')}
    </TypographyText>
  );

  const renderActionComponent = () => (
    <>
      <CommonButton
        variant="outlined"
        className="dialog-actions-button"
        sx={{
          height: '40px',
          border: `1px solid ${COLOR.secondary[16]}`,
          padding: '10px 16px',
          borderRadius: '8px',
        }}
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.indigo.default,
        }}
        onClick={() => setOpenLogoutAllDevicesDialog(false)}
      >
        {t('cancel')}
      </CommonButton>
      <CommonButton
        variant="contained"
        customStyle={{
          height: '40px',
          borderRadius: '8px',
          padding: '10px 16px',
        }}
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
        onClick={() => handleConfirmLogoutDevices(TYPE_LOGOUT_SESSION.ALL)}
      >
        {t('logout')}
      </CommonButton>
    </>
  );

  return (
    <>
      <StyledWapper>
        {!dialogDevice && (
          <div className="header-manager-wrapper">
            <Typography className="title">{t('manageDevice')}</Typography>
          </div>
        )}
        <Card className="device-list">
          <StyledManagerDevice
            isPopup={isPopup}
            isExceedDevices={
              isExceedDevices || status === DEVICE_STATUS.RESTRICTED
            }
            dialogDevice={dialogDevice}
          >
            <div className="manager-device-header">
              {dialogDevice && (
                <div className="noti-error">
                  <ErrorRoundedIcon className="info-error-icon" />
                  <TypographyText
                    variant="body-12-regular"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(
                        t('deviceLimitExceeded', {
                          numberDevices: maxDevices?.toLocaleString(language),
                        }),
                      ),
                    }}
                  />
                </div>
              )}
              <div className="display-flex">
                <div className="info-device">
                  <div className="title-device">{t('mydevice')}:</div>
                  <div className="total-device">
                    {loginDevices?.toLocaleString(language)}/
                    {maxDevices?.toLocaleString(language)} {t('device')}
                  </div>
                </div>
                {showLogoutAllDevicesDesktop && (
                  <CommonButton
                    variant="outlined"
                    className="dialog-actions-button"
                    sx={{
                      border: `1px solid ${COLOR.secondary[16]}`,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      height: '40px',
                    }}
                    textProps={{
                      variantText: 'body-14-semibold',
                      color: COLOR.indigo.default,
                    }}
                    onClick={() => {
                      setOpenLogoutAllDevicesDialog(true);
                    }}
                  >
                    {t('logoutAllDevices')}
                  </CommonButton>
                )}
              </div>
            </div>
            {loading ? (
              <CircularProgress />
            ) : (
              <div className="table-device">
                <DeviceItem
                  deviceItem={currentDevice}
                  isPopup={isPopup}
                  fingerprint={fingerprint}
                  onClickLogout={() =>
                    handleShowConfirmLogoutDialog(currentDevice)
                  }
                  className="current-device"
                />

                {Boolean(otherDevices.length) && (
                  <DeviceSelect
                    selectAll={allOtherDeviceSelected}
                    hasSelectDevice={hasSelectDevice}
                    numberDevices={selectedDevices.length}
                    onSelectAll={handleSelectAll}
                    onSelect={handleSelectOtherDevice}
                    onCancel={handleCancelSelectOtherDevice}
                    onLogout={handleShowConfirmLogoutMulteDialog}
                  />
                )}
                {sortBy(
                  [...otherDevices],
                  (item) => fingerprint !== item?.fingerprint,
                )?.map((device) => (
                  <DeviceItem
                    deviceItem={device}
                    isPopup={isPopup}
                    fingerprint={fingerprint}
                    onClickLogout={() => handleShowConfirmLogoutDialog(device)}
                    selected={selectedDevices.includes(device.id)}
                    onSelect={() => handleSelectDevice(device.id)}
                    hasSelectDevice={hasSelectDevice}
                  />
                ))}
              </div>
            )}
          </StyledManagerDevice>
        </Card>
        {showLogoutAllDevicesMobile && (
          <CommonButton
            variant="outlined"
            className="dialog-actions-button"
            sx={{
              border: `1px solid ${COLOR.secondary[16]}`,
              padding: '10px 16px',
              borderRadius: '8px',
              backgroundColor: COLOR.white,
              width: '100%',
              marginTop: '16px',
              height: '40px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.indigo.default,
            }}
            onClick={() => {
              setOpenLogoutAllDevicesDialog(true);
            }}
          >
            {t('logoutAllDevices')}
          </CommonButton>
        )}
      </StyledWapper>

      <CommonDialog
        width="512px"
        fullWidth
        open={openLogoutAllDevicesDialog}
        onClose={() => setOpenLogoutAllDevicesDialog(false)}
        title={t('logoutAllDevices')}
        contentComponent={renderContent()}
        actionComponent={renderActionComponent()}
      />

      <NotificationDialog
        name="ssml"
        dialogDevice
        title={t('confirmLogoutDevicesTitle')}
        variant="warning"
        open={showConfirmLogoutDialog}
        onClose={handleCloseConfirmLogoutDialog}
        actionComponent={renderConfirmLogoutDevicesAction(
          handleCloseConfirmLogoutDialog,
          handleConfirmLogoutDevices,
        )}
        contentComponent={renderContentLogoutDevice()}
      />

      {showRequestLogutDevice && (
        <OTPConfirm
          open={showRequestLogutDevice}
          onClose={() => setShowRequestLogutDevice(false)}
          onSubmit={logoutDeviceWithOTP}
          destination={userInfo?.email}
          onResendOTP={handleConfirmLogoutDevices}
        />
      )}
    </>
  );
};

export default NewManageDevice;
